import React, { useCallback, useContext, useMemo } from 'react';
import { Dropdown, Image, Layout, Space, Typography } from 'antd';
import classNames from 'classnames';
import Navbar from './Navbar';
import { MenuInfo } from 'rc-menu/lib/interface';
import LayoutContext, { ILayoutContext } from '@/context/AdminLayoutContext';
import { currentUserInitialState, currentUserVar, setCurrentUser } from '@/graphql/operations/user';
import LocalStorageService from '@/service/LocalStorage';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useReactiveVar } from '@apollo/client';
import {
  ApiOutlined,
  ClockCircleOutlined,
  DownOutlined,
  GiftOutlined,
  LogoutOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import styles from './AdminUserAppLayout.module.pcss';
import { ItemType } from 'antd/es/menu/interface';
import { useImmer } from 'use-immer';
import { Loader } from '@/components';
import { Link } from 'react-router-dom';
import { clearAdminFilters } from '@/utils/clearAdminFilters';

const { Header } = Layout;
const GlobalRewardPreferences = React.lazy(() => import('@/pages/OrganizationPreferences/Rewards'));
const InviteAdministratorsModal = React.lazy(() => import('@/pages/OrganizationPreferences/InviteAdministrators'));
const AccountDataModal = React.lazy(() => import('@/pages/OrganizationPreferences/AccountData'));
const UICustomizationModal = React.lazy(() => import('@/pages/OrganizationPreferences/UICustomization'));

const DesktopHeader = () => {
  const navigate = useNavigate();
  const [state, setState] = useImmer({
    rewardsModalOpen: false,
    inviteAdministratorsModalOpen: false,
    dropDownVisible: false,
    accountDataModalOpen: false,
    UICustomizationModalOpen: false,
  });
  const currentUser = useReactiveVar(currentUserVar);
  const layoutContext = useContext<ILayoutContext>(LayoutContext);
  const client = useApolloClient();

  const onUserMenuClick = async (e: MenuInfo) => {
    if (e.key === 'logout') {
      try {
        await client.clearStore();
        setCurrentUser(currentUserInitialState);
        LocalStorageService.clear();
        navigate('/signin', { replace: true });
      } catch {
        return;
      }
    }
    if (e.key === 'rewards') {
      onOpenRewardsSettingModal(true);
    }

    if (e.key === 'invite-administrators') {
      onOpenInviteAdministratorsModal(true);
    }
    if (e.key === 'account') {
      onOpenAccountSettingModal(true);
    }

    if (e.key === 'timeKeeping') {
      navigate('/preferences/widgets/timekeeping', { replace: true });
    }

    if (e.key === 'customization') {
      onOpenUICustomizationModal(true);
    }
  };

  const onOpenRewardsSettingModal = useCallback(
    (isOpen: boolean) => {
      setState((draft) => {
        draft.rewardsModalOpen = isOpen;
      });
    },
    [setState]
  );

  const onOpenAccountSettingModal = useCallback(
    (isOpen: boolean) => {
      setState((draft) => {
        draft.accountDataModalOpen = isOpen;
      });
    },
    [setState]
  );

  const onOpenInviteAdministratorsModal = useCallback(
    (isOpen: boolean) => {
      setState((draft) => {
        draft.inviteAdministratorsModalOpen = isOpen;
      });
    },
    [setState]
  );

  const onOpenUICustomizationModal = useCallback(
    (isOpen: boolean) => {
      setState((draft) => {
        draft.UICustomizationModalOpen = isOpen;
      });
    },
    [setState]
  );

  const handleVisibleChange = (visible: boolean) => {
    setState((draft) => {
      draft.dropDownVisible = visible;
    });
  };

  const menuItems = useMemo<ItemType[]>(() => {
    return [
      {
        key: 'email',
        label: currentUser?.email,
        disabled: true,
        className: styles.menuItem,
      },
      {
        type: 'divider',
      },
      {
        key: 'rewards',
        label: 'Rewards Settings',
        icon: <GiftOutlined />,
        className: styles.menuItem,
      },
      {
        key: 'widgets',
        label: 'Widgets',
        children: [
          {
            key: 'timeKeeping',
            label: 'Time Keeping',
            icon: <ClockCircleOutlined />,
          },
        ],
        icon: <ApiOutlined />,
      },
      {
        key: 'invite-administrators',
        label: 'Invite Administrators',
        icon: <UsergroupAddOutlined />,
        className: styles.menuItem,
      },
      {
        key: 'customization',
        label: 'Customization',
        icon: <SettingOutlined />,
        className: styles.menuItem,
      },
      {
        key: 'logout',
        label: 'Logout',
        icon: <LogoutOutlined />,
      },
    ];
  }, [currentUser?.email]);

  return (
    <>
      <Header className={styles.header}>
        <div className={classNames(styles.logo, styles[layoutContext.layoutConfig?.title.name])}>
          <Link to="/" onClick={() => clearAdminFilters('schedulingFilters')}>
            <Image
              src={
                layoutContext.layoutConfig.darkLogo
                  ? layoutContext.layoutConfig.darkLogo
                  : layoutContext.layoutConfig.logo
              }
              alt="logo"
              preview={false}
            />
          </Link>
        </div>
        <Navbar />
        <div className={styles.right}>
          <Dropdown
            trigger={['hover']}
            menu={{
              items: menuItems,
              onClick: onUserMenuClick,
            }}
            onOpenChange={handleVisibleChange}
            open={state.dropDownVisible}
            className={styles.action}
          >
            <Space align="center">
              <Typography.Paragraph className={styles.userName}>
                {currentUser?.name || currentUser.email}
              </Typography.Paragraph>
              <DownOutlined style={{ color: '#828282' }} />
            </Space>
          </Dropdown>
        </div>
      </Header>
      {state.rewardsModalOpen ? (
        <React.Suspense fallback={<Loader />}>
          <GlobalRewardPreferences isModalVisible={state.rewardsModalOpen} toggleModal={onOpenRewardsSettingModal} />
        </React.Suspense>
      ) : null}
      {state.inviteAdministratorsModalOpen ? (
        <React.Suspense fallback={<Loader />}>
          <InviteAdministratorsModal
            isModalVisible={state.inviteAdministratorsModalOpen}
            toggleModal={onOpenInviteAdministratorsModal}
          />
        </React.Suspense>
      ) : null}
      {state.accountDataModalOpen ? (
        <React.Suspense fallback={<Loader />}>
          <AccountDataModal isModalVisible={state.accountDataModalOpen} toggleModal={onOpenAccountSettingModal} />
        </React.Suspense>
      ) : null}
      {state.UICustomizationModalOpen ? (
        <React.Suspense fallback={<Loader />}>
          <UICustomizationModal isOpen={state.UICustomizationModalOpen} toggleModal={onOpenUICustomizationModal} />
        </React.Suspense>
      ) : null}
    </>
  );
};

export default DesktopHeader;
